import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  useToast,
} from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';
import { cn } from '@ais-ui/utils';

const scaleFormSchema = z.object({
  targetScale: z.number().min(75).max(325),
  predictorDuration: z.number().min(1).nullable(),
  sogVectorDuration: z.number().min(1).nullable(),
});

type ScaleTargetFormValues = z.infer<typeof scaleFormSchema>;

export const TargetScaleForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    targetScale: settings['ribbonPlugin.settings']?.target_scale
      ? parseFloat(settings['ribbonPlugin.settings'].target_scale)
      : 100,
    predictorDuration: settings['ribbonPlugin.settings']?.predictor_time
      ? parseFloat(settings['ribbonPlugin.settings'].predictor_time)
      : null,
    sogVectorDuration: settings['ribbonPlugin.settings']?.sog_time
      ? parseFloat(settings['ribbonPlugin.settings'].sog_time)
      : null,
  };

  const form = useForm<ScaleTargetFormValues>({
    resolver: zodResolver(scaleFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: ScaleTargetFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        target_scale: data.targetScale.toString(),
        predictor_time: data.predictorDuration ? data.predictorDuration : 360,
        sog_time: data.sogVectorDuration ? data.sogVectorDuration : 900,
        scale_on: true,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-scale-target-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapTrackSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-scale-target-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapTrackFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <FormField
            key="targetScale"
            control={form.control}
            name="targetScale"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsMapTrackScaleLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsMapTrackScaleDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-target-scale"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    min={75}
                    max={325}
                    type="number"
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);

                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            key="predictorDuration"
            control={form.control}
            name="predictorDuration"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsMapTrackPredictorDurationLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsMapTrackPredictorDurationDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-target-predictor"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    min={1}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange(null);
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);

                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            key="sogVectorDuration"
            control={form.control}
            name="sogVectorDuration"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsMapTrackSOGVectorDurationLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsMapTrackSOGVectorDurationDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-target-sog-vector"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    min={1}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange(null);
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);

                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-target-scale-save-button"
            onClick={() => {
              if (Object.keys(form.formState.errors).length > 0) {
                toast({
                  className: 'validation-error-toast',
                  title: messages.genericToastTitleError,
                  description: messages.settingsTargetScaleValidationError,
                  variant: 'destructive',
                });
              } else {
                form.handleSubmit(onSubmit);
              }
            }}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
